import { z } from 'zod';
import { defineForm } from '../types';
import { ENTRA_USER_REQUIRED, pickEntraUser } from '../catalogues/entra-user';

/**
 * A generic, placeholder request form.
 *
 * The *content* here is deliberately bland — the real onboarding/offboarding forms
 * arrive later, one file each, following exactly this shape. What matters now is the
 * pattern: a Zod schema as the single source of truth, plus rendering hints.
 */

export const REQUEST_TYPES = [
  'onboard-user',
  'offboard-user',
  'onboard-computer',
  'offboard-computer',
  'other',
] as const;

/** The Entra questions appear only for this request type. */
const whenOnboardingUser = { field: 'requestType', equals: 'onboard-user' } as const;

/**
 * The Entra attributes an onboarding request asks for: the creation trio plus what a
 * technician needs on day one. Picked from the shared catalogue, optional here because a
 * hidden field never reaches the payload; the refinement below requires the trio back
 * once they are on screen.
 */
const entraUser = pickEntraUser(
  [
    'userPrincipalName',
    'givenName',
    'surname',
    'jobTitle',
    'department',
    'mobilePhone',
    'usageLocation',
  ],
  { optional: true, visibleWhen: whenOnboardingUser },
);

export const genericRequestSchema = z
  .object({
    subjectName: z
      .string()
      .trim()
      .min(1, 'Tell us who or what this request is about')
      .max(200, 'Keep this under 200 characters'),

    requestType: z.enum(REQUEST_TYPES, { error: 'Choose a request type' }),

    // An HTML date input submits '' when left blank. Accept that and turn it into
    // "not provided", so the stored document never contains an empty string.
    neededBy: z
      .union([
        z.iso.date({ error: 'Use the date picker (YYYY-MM-DD)' }),
        z.literal('').transform(() => undefined),
      ])
      .optional(),

    description: z
      .string()
      .trim()
      .min(10, 'Please give at least a sentence of detail')
      .max(4000, 'Keep this under 4000 characters'),

    contactEmail: z.email('Enter a valid email address'),

    // Only asked when onboarding a user; see `entraUser` above.
    ...entraUser.shape,
  })
  .superRefine((value, ctx) => {
    if (value.requestType !== 'onboard-user') return;

    for (const field of ENTRA_USER_REQUIRED) {
      if (value[field] === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: [field],
          message: 'Required to create the user',
        });
      }
    }
  });

export type GenericRequestInput = z.input<typeof genericRequestSchema>;
export type GenericRequest = z.output<typeof genericRequestSchema>;

export const genericRequest = defineForm({
  id: 'generic-request',
  version: '2.0.0',
  customers: 'all-customers',
  title: 'General IT request',
  description: 'Placeholder form used while the real request types are being designed.',
  schema: genericRequestSchema,
  caseTitle: (payload) => payload.subjectName,
  fields: [
    {
      name: 'subjectName',
      label: 'Who or what is this request about?',
      widget: 'text',
      placeholder: 'e.g. Jane Smith, or LAPTOP-0421',
    },
    {
      name: 'requestType',
      label: 'Request type',
      widget: 'select',
      options: [
        { value: 'onboard-user', label: 'Onboard a user' },
        { value: 'offboard-user', label: 'Offboard a user' },
        { value: 'onboard-computer', label: 'Onboard a computer' },
        { value: 'offboard-computer', label: 'Offboard a computer' },
        { value: 'other', label: 'Something else' },
      ],
    },
    {
      name: 'neededBy',
      label: 'Needed by',
      widget: 'date',
      helpText: 'Optional. When does this need to be done?',
    },
    {
      name: 'description',
      label: 'Details',
      widget: 'textarea',
      helpText: 'Anything the technician needs to know to complete this request.',
    },
    {
      name: 'contactEmail',
      label: 'Contact email',
      widget: 'email',
      helpText: 'Who should we contact with questions about this request?',
    },

    // The Entra questions, shown only when the request is to onboard a user. Drawn from
    // the same catalogue as the Create an Entra User form, so the two cannot drift.
    ...entraUser.fields,
  ],
});
